import s from '../s';
import {linoSectionShown} from '../selection/lino';
import {gradYearMounted} from '../selection/grad-year';
import {categoriesMounted} from '../selection/doc-size';
import {mouldingSectionShown} from '../selection/moulding';
import {matSectionShown} from '../selection/mat';
import {insigniaSectionShown} from '../selection/insignia';
import {glassSectionShown} from '../selection/glass';
import {getCategoryName, getCategoryOptions} from '../components/doc-sizes-and-categories/category-utils';
import {allowsCategoryOption} from '../components/doc-sizes-and-categories/document-sizing';
import {redesign} from './redesign-store';

/**
 * Which steps the redesign shows and in what order (PRODUCT-PAGE-REDESIGN-AB-TEST.md, "Steps"): a step shows only where the
 * control shows that section, so a page gets 3 to 6 numbered steps. Size questions (the degree, the year, or the control's
 * categories) put the school and them in a "Make sure it fits" group ahead of the "Make it yours" group.
 */

export type StepId = 'school' | 'degree' | 'year' | 'frame' | 'mat' | 'glass' | `category-${number}`;

export type Step = {
    id: StepId;
    number: number;
    /** In the "Make sure it fits" group. */
    fit: boolean;
    /** The control category a category step carries. */
    categoryId?: number;
};

export type StepPlan = {
    /** The size is asked (degree, year or categories): "Build your frame" with its two groups. */
    sizeQuestions: boolean;
    steps: Step[];
};

/** A control Category renders (category.tsx's own guard, its options filtered as it filters them). */
export const categoryStepShown = (categoryId: number): boolean => {
    const options = getCategoryOptions(s.prod.categories, categoryId).filter(option => allowsCategoryOption(s.prod, option));
    return s.ready && !!getCategoryName(options) && !s.ps.is_caf && !(s.docsizes_enabled && s.docsizes_available);
};

/** The graduation year sits in the school step: the product shows the field, and the year does not set the size. */
export const gradYearInSchoolStep = (): boolean => gradYearMounted() && !redesign.yearShown;

export function stepPlan(): StepPlan {
    const categoryIds = categoriesMounted() ? s.ui.categoryIds.filter(categoryStepShown) : [];
    const sizeQuestions = redesign.degreeShown || redesign.yearShown || categoryIds.length > 0;
    const ids: {id: StepId; fit: boolean; categoryId?: number}[] = [];

    if (linoSectionShown() || gradYearInSchoolStep()) {
        ids.push({id: 'school', fit: sizeQuestions});
    }
    if (redesign.degreeShown) {
        ids.push({id: 'degree', fit: true});
    }
    if (redesign.yearShown) {
        ids.push({id: 'year', fit: true});
    }
    for (const categoryId of categoryIds) {
        ids.push({id: `category-${categoryId}`, fit: true, categoryId});
    }
    if (mouldingSectionShown()) {
        ids.push({id: 'frame', fit: false});
    }
    if (matSectionShown() || insigniaSectionShown()) {
        ids.push({id: 'mat', fit: false});
    }
    if (glassSectionShown()) {
        ids.push({id: 'glass', fit: false});
    }

    return {sizeQuestions, steps: ids.map((step, i) => ({...step, number: i + 1}))};
}

/** "Steps 1 to 3", "Step 2". */
export const stepRange = (numbers: number[]): string => (numbers.length === 0 ? '' : numbers.length === 1 ? `Step ${numbers[0]}` : `Steps ${numbers[0]} to ${numbers[numbers.length - 1]}`);

/** The heading's second line: which steps fit the document and which are set to the photo. */
export function stepsIntro(plan: StepPlan): {wide: string; narrow: string} {
    if (!plan.sizeQuestions) {
        return {wide: 'Already set to the frame in the photo. Change any step below.', narrow: 'Already set to the frame in the photo. Change any step below.'};
    }
    const fit = plan.steps.filter(step => step.fit).map(step => step.number);
    const yours = plan.steps.filter(step => !step.fit).map(step => step.number);
    const fitText = `${stepRange(fit)} ${fit.length === 1 ? 'makes' : 'make'} sure it fits.`;
    const yoursText = yours.length === 0 ? '' : ` ${stepRange(yours)} ${yours.length === 1 ? 'is' : 'are'} already set to the frame in the photo.`;
    return {wide: `${fitText}${yoursText}`, narrow: `${stepRange(fit)} ${fit.length === 1 ? 'sets' : 'set'} your size.`};
}
